/**
 * Localizer for pokersolver hand descriptions (English) → Korean.
 *
 * Pokersolver's `hand.descr` is a human-readable English string like:
 *   - "Royal Flush"
 *   - "Straight Flush"
 *   - "Four of a Kind, A's"
 *   - "Full House, A's over K's"
 *   - "Flush, As High"
 *   - "Straight, A High"
 *   - "Three of a Kind, A's"
 *   - "Two Pair, A's & K's"
 *   - "Pair, A's"
 *   - "A High"
 *
 * Mixing those English strings with Korean player names in the winner
 * line reads awkwardly; this function maps them to poker-lingo Korean
 * ("투페어 A·K", "풀하우스 A·K" …). Unknown shapes fall back to the
 * original English so we never crash on a surprise format.
 */

const RANK_RE = /[AKQJ]|10|[2-9]/;

function cleanRank(raw: string): string {
  const trimmed = raw.trim();
  // Strip any trailing suit glyph pokersolver occasionally includes
  // ("As" for ace of spades, "Kh", "Td" etc.).
  const match = trimmed.match(RANK_RE);
  if (match) return match[0] === 'T' ? '10' : match[0];
  return trimmed.toUpperCase();
}

export function toKoreanHandDescription(desc: string | undefined): string | null {
  if (!desc) return null;
  const text = desc.trim();
  if (!text) return null;

  if (/^Royal Flush/i.test(text)) return '로열 플러시';
  if (/^Straight Flush/i.test(text)) return '스트레이트 플러시';

  let m = text.match(/^Four of a Kind,\s*(.+?)'s/i);
  if (m) return `포카드 ${cleanRank(m[1])}`;

  m = text.match(/^Full House,\s*(.+?)'s\s*over\s*(.+?)'s/i);
  if (m) return `풀하우스 ${cleanRank(m[1])}·${cleanRank(m[2])}`;

  m = text.match(/^Flush,\s*(.+?)\s*High/i);
  if (m) return `플러시 ${cleanRank(m[1])} 하이`;

  m = text.match(/^Straight,\s*(.+?)\s*High/i);
  if (m) return `스트레이트 ${cleanRank(m[1])} 하이`;

  m = text.match(/^Three of a Kind,\s*(.+?)'s/i);
  if (m) return `트리플 ${cleanRank(m[1])}`;

  m = text.match(/^Two Pair,\s*(.+?)'s\s*&\s*(.+?)'s/i);
  if (m) return `투페어 ${cleanRank(m[1])}·${cleanRank(m[2])}`;

  m = text.match(/^Pair,\s*(.+?)'s/i);
  if (m) return `원페어 ${cleanRank(m[1])}`;

  // "A High" / "K, Q, J High" etc.
  m = text.match(/^([AKQJ]|10|[2-9])[^H]*High/i);
  if (m) return `하이카드 ${cleanRank(m[1])}`;

  return text;
}
